import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, Gauge, Mail, CalendarCheck2, ClipboardCheck } from "lucide-react";
import { track } from "@/lib/analytics";
import { openCalendly } from "@/integrations/calendly";
import { STAGE_ORDER, STAGE_META, QUESTIONS, computeResults, buildPlan, scoreLabel } from "@/lib/scorecardScoring";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPES = ["Individual agent", "Team", "Brokerage"];

const scoreColor = (s) =>
  s >= 80 ? "text-emerald-300" : s >= 55 ? "text-amber-300" : s >= 30 ? "text-orange-300" : "text-red-300";
const barColor = (s) =>
  s >= 80 ? "bg-emerald-400" : s >= 55 ? "bg-amber-400" : s >= 30 ? "bg-orange-400" : "bg-red-400";

const HEADLINE_BY_LABEL = {
  "Strong Foundation": "on a strong foundation",
  "Some Process Gaps": "working, with some process gaps",
  "Significant Improvement Opportunities": "showing significant improvement opportunities",
  "High-Priority Process Gaps": "carrying high-priority gaps that may be costing you opportunities",
};

// Scorecard -> audit handoff: only semantically equivalent fields are carried,
// via sessionStorage (never the URL), and remain fully editable in the audit form.
const AUDIT_PREFILL_KEY = "ivibes_audit_prefill";
const RESPONSE_TIME_MAP = {
  "Under 5 minutes": "Under 5 minutes",
  "Within 30 minutes": "5–30 minutes",
  "Same day": "Same day",
  "Next day or later": "Next day or later",
};
const AFTER_HOURS_MAP = {
  "They get an immediate response, automated or manual": "Answered right away",
  "I respond first thing the next morning": "Handled the next business day",
  "They're often missed entirely": "Often missed",
};
const ATTEMPTS_MAP = { "Usually none after the first": "0–1" };
const BOOKING_MAP = {
  "They can self-schedule instantly through a link": "Online scheduling link",
  "A quick back-and-forth, usually same day": "Manual back-and-forth",
  "It usually takes a few exchanges": "Manual back-and-forth",
  "There's no set process": "No set process",
};

export default function Scorecard() {
  const [screen, setScreen] = useState("intro"); // intro | quiz | results
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // qid -> {points, text}
  const [results, setResults] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [consent, setConsent] = useState(false);
  const [emailState, setEmailState] = useState("idle"); // idle | sending | sent | error
  const submittedRef = useRef(false);

  useEffect(() => {
    track("scorecard_opened");
  }, []);

  const payloadBase = (ans, r) => ({
    answers: Object.fromEntries(QUESTIONS.map((q) => [q.id, ans[q.id]?.text ?? ""])),
    stage_scores: r.stageScores,
    overall_score: r.overall,
    top_stages: r.topStages,
  });

  const answer = (points, optionText) => {
    const q = QUESTIONS[qIndex];
    const next = { ...answers, [q.id]: { points, text: optionText } };
    setAnswers(next);
    if (qIndex === 0) track("scorecard_started");
    if (qIndex < QUESTIONS.length - 1) {
      setQIndex(qIndex + 1);
    } else {
      const pointMap = Object.fromEntries(Object.entries(next).map(([k, v]) => [k, v.points]));
      const r = computeResults(pointMap);
      setResults(r);
      setScreen("results");
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (!submittedRef.current) {
        submittedRef.current = true;
        axios
          .post(`${API}/scorecard/submissions`, { ...payloadBase(next, r), email: null })
          .then(({ data }) => setSubmissionId(data.submission_id))
          .catch(() => {});
      }
    }
  };

  const signal = (type) => {
    if (submissionId) {
      axios.post(`${API}/scorecard/submissions/${submissionId}/signal`, { signal: type }).catch(() => {});
    }
  };

  const goAudit = () => {
    signal("audit_clicked");
    try {
      const t = (qid) => answers[qid]?.text;
      const picks = {};
      const challenges = [];
      if (RESPONSE_TIME_MAP[t("q2")]) picks.response_time = RESPONSE_TIME_MAP[t("q2")];
      if (AFTER_HOURS_MAP[t("q3")]) picks.after_hours = AFTER_HOURS_MAP[t("q3")];
      if (ATTEMPTS_MAP[t("q5")]) picks.follow_up_attempts = ATTEMPTS_MAP[t("q5")];
      if (BOOKING_MAP[t("q7")]) picks.booking_process = BOOKING_MAP[t("q7")];
      if (["No", "I mostly go by feel"].includes(t("q4"))) challenges.push("Qualifying which leads are ready to move");
      if (["Same day", "Next day or later"].includes(t("q2"))) challenges.push("Responding to new inquiries quickly");
      if (["Usually none after the first", "1–2 attempts"].includes(t("q5"))) challenges.push("Following up with leads consistently");
      if (["No", "I could piece it together manually"].includes(t("q8"))) challenges.push("Knowing what happened to each lead");
      const prefill = { picks, challenges };
      if (firstName.trim()) prefill.first_name = firstName.trim();
      if (email.trim()) prefill.business_email = email.trim();
      if (businessType) prefill.operation_type = businessType;
      sessionStorage.setItem(AUDIT_PREFILL_KEY, JSON.stringify(prefill));
    } catch {}
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    if (!results || !email.trim()) return;
    setEmailState("sending");
    try {
      await axios.post(`${API}/scorecard/submissions`, {
        ...payloadBase(answers, results),
        email: email.trim(),
        first_name: firstName.trim() || null,
        business_type: businessType || null,
        marketing_consent: consent,
      });
      track("scorecard_email_requested");
      setEmailState("sent");
    } catch {
      setEmailState("error");
    }
  };

  const progress = Math.round(((screen === "results" ? QUESTIONS.length : qIndex) / QUESTIONS.length) * 100);

  return (
    <div data-testid="scorecard-page" className="min-h-screen bg-[#0B0F19]">
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" data-testid="scorecard-logo" className="flex items-center gap-2.5">
            <span className="bg-white/95 rounded-xl px-2 py-1 flex items-center">
              <img src="/ivibes-cloud.png" alt="iVIBES logo" className="h-7 w-auto" />
            </span>
            <span className="font-heading text-xl tracking-tight font-extrabold">
              <span className="text-cyan-400">i</span>VIBES
              <span className="text-xl font-medium text-slate-200 ml-1.5 tracking-normal">Consulting</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="scorecard-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-cyan-300 transition-colors duration-200"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Back to Home</span>
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-14 md:py-20">
        <AnimatePresence mode="wait">
          {screen === "intro" && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}
              className="text-center"
            >
              <div className="mx-auto h-16 w-16 rounded-2xl bg-cyan-500/10 text-cyan-300 flex items-center justify-center mb-7">
                <Gauge size={30} strokeWidth={1.75} />
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
                The <span className="text-gradient">Lead Leakage Scorecard</span>
              </h1>
              <p className="mt-5 text-lg md:text-xl text-slate-200 leading-relaxed">
                A free, five-minute assessment that shows you exactly where buyer and seller
                opportunities may be slipping — across capture, response, qualification, follow-up,
                booking, and tracking.
              </p>
              <p className="mt-4 text-base text-slate-300 leading-relaxed">
                Eight quick questions. Instant, transparent scoring. Three prioritized
                recommendations and a 7-day improvement plan.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-2.5">
                <span className="rounded-full border border-white/10 bg-white/[0.04] text-slate-300 text-xs font-semibold px-3.5 py-1.5">8 questions</span>
                <span className="rounded-full border border-white/10 bg-white/[0.04] text-slate-300 text-xs font-semibold px-3.5 py-1.5">About 5 minutes</span>
                <span className="rounded-full border border-white/10 bg-white/[0.04] text-slate-300 text-xs font-semibold px-3.5 py-1.5">Free, no obligation</span>
              </div>
              <button
                type="button"
                data-testid="scorecard-start-button"
                onClick={() => setScreen("quiz")}
                className="group mt-10 inline-flex items-center justify-center gap-2 rounded-full px-9 py-4 bg-indigo-600 hover:bg-indigo-500 font-semibold text-white text-lg transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_24px_rgba(99,102,241,0.45)]"
              >
                Start the Scorecard
                <ArrowRight size={19} className="transition-transform duration-200 group-hover:translate-x-1" />
              </button>
              <p className="mt-5 text-xs text-slate-500">
                Your answers stay private. Scores are based entirely on your own input.
              </p>
            </motion.div>
          )}

          {screen === "quiz" && (
            <motion.div
              key={`q-${qIndex}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
            >
              <div className="mb-8">
                <div className="flex items-center justify-between text-xs font-semibold text-slate-400 mb-2">
                  <span data-testid="scorecard-question-count">Question {qIndex + 1} of {QUESTIONS.length}</span>
                  <span>{STAGE_META[QUESTIONS[qIndex].stage].label}</span>
                </div>
                <div
                  data-testid="scorecard-progress"
                  role="progressbar"
                  aria-valuenow={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  className="h-1.5 rounded-full bg-white/10 overflow-hidden"
                >
                  <div className="h-full bg-gradient-to-r from-cyan-400 to-indigo-500 transition-[width] duration-300" style={{ width: `${progress}%` }} />
                </div>
              </div>

              <h2 data-testid={`scorecard-question-${qIndex + 1}`} className="font-heading font-bold text-2xl sm:text-3xl text-white leading-snug">
                {QUESTIONS[qIndex].text}
              </h2>

              <div className="mt-8 space-y-3">
                {QUESTIONS[qIndex].options.map((opt, i) => (
                  <button
                    key={opt}
                    type="button"
                    data-testid={`scorecard-option-${qIndex + 1}-${i}`}
                    onClick={() => answer(3 - i, opt)}
                    className="w-full text-left rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4 text-base text-slate-200 font-medium transition-[border-color,background-color,transform] duration-200 hover:border-cyan-400/40 hover:bg-white/[0.05] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  >
                    {opt}
                  </button>
                ))}
              </div>

              {qIndex > 0 && (
                <button
                  type="button"
                  data-testid="scorecard-back-button"
                  onClick={() => setQIndex(qIndex - 1)}
                  className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-cyan-300 transition-colors duration-200"
                >
                  <ArrowLeft size={15} /> Previous question
                </button>
              )}
            </motion.div>
          )}

          {screen === "results" && results && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div data-testid="scorecard-results" className="text-center">
                <p className="text-xs font-bold uppercase tracking-widest text-cyan-300">Your Lead Flow Score</p>
                <p data-testid="scorecard-overall-score" className={`mt-3 font-heading font-extrabold text-6xl sm:text-7xl tracking-tight ${scoreColor(results.overall)}`}>
                  {results.overall}<span className="text-2xl text-slate-500">/100</span>
                </p>
                <p className="mt-3 text-base text-slate-300">
                  Based on your answers, your lead flow is{" "}
                  <span className={`font-semibold ${scoreColor(results.overall)}`}>
                    {HEADLINE_BY_LABEL[scoreLabel(results.overall)]}
                  </span>.
                </p>
              </div>

              <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-7">
                <h2 className="font-heading font-bold text-lg text-white mb-5">Score by stage</h2>
                <div className="space-y-4">
                  {STAGE_ORDER.map((key) => (
                    <div key={key} data-testid={`scorecard-stage-bar-${key}`}>
                      <div className="flex items-center justify-between text-sm mb-1.5">
                        <span className="font-semibold text-slate-200">{STAGE_META[key].label}</span>
                        <span className={`font-bold ${scoreColor(results.stageScores[key])}`}>
                          {results.stageScores[key]}% · {scoreLabel(results.stageScores[key])}
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                        <div className={`h-full rounded-full ${barColor(results.stageScores[key])}`} style={{ width: `${results.stageScores[key]}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
                <p data-testid="scorecard-scoring-note" className="mt-5 text-xs text-slate-500 leading-relaxed">
                  How scoring works: each answer is worth 0–3 points, and every question counts
                  equally toward your overall score out of 100. Stage scores average only their own
                  questions. Your three lowest-scoring stages become your priorities (ties break in
                  framework order: Capture, Respond, Qualify, Follow Up, Book, Track). Scores
                  reflect your own answers — illustrative, not measured performance.
                </p>
              </div>

              <div className="mt-8">
                <h2 className="font-heading font-bold text-xl text-white mb-4">Your three biggest opportunities</h2>
                <div className="space-y-3.5">
                  {results.topStages.map((key, i) => (
                    <div key={key} data-testid={`scorecard-recommendation-${i}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                      <p className="text-xs font-bold uppercase tracking-widest text-indigo-300 mb-1.5">
                        Priority {i + 1} · {STAGE_META[key].label}
                      </p>
                      <p className="text-sm text-slate-200 leading-relaxed">{STAGE_META[key].recommendation}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-7">
                <h2 className="font-heading font-bold text-xl text-white mb-4">Your 7-day improvement plan</h2>
                <ol className="space-y-3.5">
                  {buildPlan(results.topStages).map((text, i) => (
                    <li key={i} data-testid={`scorecard-plan-day-${i + 1}`} className="flex gap-3.5">
                      <span className="h-7 w-7 shrink-0 rounded-full bg-cyan-500/15 text-cyan-300 text-xs font-bold flex items-center justify-center mt-0.5">
                        {i + 1}
                      </span>
                      <p className="text-sm text-slate-200 leading-relaxed">{text}</p>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="mt-8 rounded-2xl border border-indigo-400/30 bg-indigo-500/[0.06] p-6 sm:p-7">
                {emailState === "sent" ? (
                  <div data-testid="scorecard-email-success" className="flex items-center gap-3 text-emerald-300">
                    <ClipboardCheck size={20} />
                    <p className="text-sm font-semibold">Your scorecard report is on its way to {email}.</p>
                  </div>
                ) : (
                  <form onSubmit={sendEmail}>
                    <h3 className="font-heading font-bold text-lg text-white">Want a copy of your results?</h3>
                    <p className="mt-1.5 text-sm text-slate-300">
                      Get your scores, recommendations, and 7-day plan by email. Optional — your
                      results above are yours either way.
                    </p>
                    <div className="mt-4 grid sm:grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="First name (optional)"
                        aria-label="First name (optional)"
                        data-testid="scorecard-name-input"
                        className="bg-white/5 border border-white/10 rounded-full px-5 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-[box-shadow,border-color] duration-200"
                      />
                      <select
                        value={businessType}
                        onChange={(e) => setBusinessType(e.target.value)}
                        aria-label="Business type (optional)"
                        data-testid="scorecard-business-type"
                        className="bg-white/5 border border-white/10 rounded-full px-5 py-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-[box-shadow,border-color] duration-200"
                      >
                        <option value="" className="bg-slate-900">Business type (optional)</option>
                        {BUSINESS_TYPES.map((t) => (
                          <option key={t} value={t} className="bg-slate-900">{t}</option>
                        ))}
                      </select>
                    </div>
                    <div className="mt-3 flex flex-col sm:flex-row gap-3">
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@yourrealty.com"
                        aria-label="Email address for your scorecard report"
                        data-testid="scorecard-email-input"
                        className="flex-1 bg-white/5 border border-white/10 rounded-full px-5 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-[box-shadow,border-color] duration-200"
                      />
                      <button
                        type="submit"
                        data-testid="scorecard-email-button"
                        disabled={emailState === "sending"}
                        className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-indigo-600 hover:bg-indigo-500 font-semibold text-white text-sm transition-colors duration-200 disabled:opacity-60"
                      >
                        {emailState === "sending" ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                        Email Me My Results
                      </button>
                    </div>
                    <label className="mt-3.5 flex items-start gap-2.5 text-xs text-slate-400 cursor-pointer leading-relaxed">
                      <input
                        type="checkbox"
                        data-testid="scorecard-consent-checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-white/20 bg-white/5 accent-cyan-500"
                      />
                      Also send me occasional practical tips on improving lead flow. Optional —
                      not required to receive my report, and I can ask to stop anytime.
                    </label>
                    {emailState === "error" && (
                      <p data-testid="scorecard-email-error" className="mt-2.5 text-sm text-red-300">
                        Something went wrong sending your report. Please try again.
                      </p>
                    )}
                  </form>
                )}
              </div>

              <div className="mt-10 text-center">
                <p className="text-lg text-slate-200 font-medium">Ready to go deeper?</p>
                <p className="mt-1.5 text-sm text-slate-400">
                  The full audit reviews your actual lead flow — personally reviewed before delivery.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row justify-center gap-4">
                  <Link
                    to="/lead-flow-audit"
                    data-testid="scorecard-audit-link"
                    data-track="scorecard_audit_clicked"
                    onClick={goAudit}
                    className="group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 bg-indigo-600 hover:bg-indigo-500 font-semibold text-white transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_24px_rgba(99,102,241,0.45)]"
                  >
                    Get Your Free Real Estate Lead Flow Audit
                    <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                  <button
                    type="button"
                    onClick={() => { signal("booking_clicked"); openCalendly(); }}
                    data-testid="scorecard-booking-button"
                    data-track="scorecard_booking_clicked"
                    className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 border-2 border-white/25 hover:border-cyan-400/60 hover:text-cyan-300 font-semibold transition-[border-color,color,transform] duration-200 hover:-translate-y-0.5"
                  >
                    <CalendarCheck2 size={18} />
                    Book Your Lead Flow Review
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
